"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@shared/src/utils/cn";


const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogClose = DialogPrimitive.Close;

const DialogPortal = DialogPrimitive.Portal;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black-50  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const dialogContentVariants = cva(
  "fixed left-[50%] top-[50%] z-50 grid w-full translate-x-[-50%] translate-y-[-50%] gap-4 border shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
  {
    variants: {
      size: {
        sm: "max-w-sm",
        md: "max-w-md",
        lg: "max-w-lg",
        xl: "max-w-xl",
        "2xl": "max-w-2xl",
        "3xl": "max-w-3xl",
      },
      background: {
        primary: "bg-surfaces-bg-primary border-surfaces-bg-tertiary",
        secondary: "bg-surfaces-bg-secondary border-border-tertiary",
      },
      padding: {
        sm: "p-4",
        md: "md:p-6 p-4",
        lg: "p-6",
      },
      position: {
        center: "md:top-[50%] md:translate-y-[-50%]",
        "center-mobile-bottom":
          "md:top-[50%] top-[unset] md:bottom-[unset] bottom-0 md:translate-y-[-50%] translate-y-0 rounded-b-none md:rounded-2xl",
      },
      hideCloseButton: {
        true: "[&>button]:hidden",
        false: "",
      },
      scroll: {
        true: "overflow-y-auto custom-scrollbar",
        false: "",
      },
      height: {
        auto: "h-auto",
        "80vh": "h-[80vh]",
        "90vh": "h-[90vh]",
        "95vh": "max-h-[95vh]",
        "90dvh": "max-h-[90dvh]",
        "85dvh": "max-h-[85dvh]",
        responsive: "min-h-0 h-auto md:h-auto md:max-h-[90dvh] max-h-[85dvh]",
      },
      layout: {
        default: "",
        "flex-col": "flex flex-col",
        "flex-col-overflow": "flex flex-col overflow-hidden",
        standard: "space-y-4 gap-0",
        compact: "space-y-2 gap-2",
      },
    },
    defaultVariants: {
      size: "lg",
      background: "primary",
      padding: "md",
      position: "center",
      hideCloseButton: false,
      scroll: false,
      height: "auto",
      layout: "default",
    },
  },
);

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof dialogContentVariants> {}

const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  (
    { size, background, padding, position, hideCloseButton, scroll, height, layout, className, children, ...props },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          dialogContentVariants({ size, background, padding, position, hideCloseButton, scroll, height, layout }),
          className,
        )}
        {...props}
      >
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogCloseButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    onClose?: () => void;
    variant?: "default" | "border-tertiary";
  }
>(({ className, onClose, variant = "default", ...props }, ref) => {
  const borderClass = variant === "border-tertiary" ? "border-border-tertiary" : "border-surfaces-bg-tertiary";

  return (
    <button
      ref={ref}
      onClick={onClose}
      className={cn("text-text-tertiary group w-fit hover:text-text-primary", className)}
      {...props}
    >
      <X className={cn("w-10 h-10 rounded-full p-2 group-hover:border", borderClass)} />
    </button>
  );
});
DialogCloseButton.displayName = "DialogCloseButton";

const DialogHeaderTitleClose = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    title: React.ReactNode;
    onClose?: () => void;
    titleClassName?: string;
    closeButtonVariant?: "default" | "border-tertiary";
  }
>(({ className, title, onClose, titleClassName, closeButtonVariant, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col space-y-1.5 text-center sm:text-left pb-0", className)} {...props}>
    <div className="flex items-center justify-between">
      <DialogTitle className={cn("!text-large text-text-primary", titleClassName)}>{title}</DialogTitle>
      <DialogCloseButton onClose={onClose} variant={closeButtonVariant} />
    </div>
  </div>
));
DialogHeaderTitleClose.displayName = "DialogHeaderTitleClose";

const DialogHeaderCentered = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    title?: React.ReactNode;
    description?: string;
    onClose?: () => void;
    closeButtonVariant?: "default" | "border-tertiary";
  }
>(({ className, title, description, onClose, closeButtonVariant, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left shrink-0 pb-4", className)}
    {...props}
  >
    <DialogCloseButton onClose={onClose} variant={closeButtonVariant} className="self-end" />
    <DialogTitle className="sr-only">{typeof title === "string" ? title : "Dialog"}</DialogTitle>
    <div className="flex flex-col items-center justify-center gap-2">
      {title && <h5 className="text-center text-text-primary">{title}</h5>}
      {description && <p className="text-center text-text-secondary">{description}</p>}
    </div>
  </div>
));
DialogHeaderCentered.displayName = "DialogHeaderCentered";

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogCloseButton,
  DialogHeaderTitleClose,
  DialogHeaderCentered,
};
